"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { Reveal } from "@/components/ui/Reveal";
import { SplitReveal } from "@/components/motion";
import { TREATMENT_HERO_IMAGES } from "@/lib/imagery";

/**
 * SI-07 pinned protocol sequence: the homepage signature moment, carrying
 * The Silk Protocol (the complete non-surgical approach to facial ageing).
 *
 * Left column: five narrative blocks (Consult, then four strands ordered
 * deepest to most superficial: Ultraformer, Volnewmer, anti-wrinkle,
 * dermal filler) scrolling normally, each with an IntersectionObserver
 * sentinel band at the vertical centre of the viewport. Right column: a
 * position-sticky full-height media panel; as each block crosses
 * centre-viewport the active image wipes in via SI-02's clip reveal. A
 * vertical gold progress hairline fills per block.
 *
 * Copy rules (homepage revision brief):
 * - The Silk Protocol is described as a protocol or plan, never "the
 *   non-surgical facelift" as a proper noun; "non-surgical" may appear
 *   descriptively.
 * - Weaving language ("one weave", strand numbering) lives in the section
 *   frame only; every treatment block stays plain and clinical.
 * - No medicine brand names; "anti-wrinkle injections" and "dermal
 *   filler" stay generic. No outcomes promised: the strongest claims are
 *   mechanism descriptions. No week intervals, session counts, or
 *   combined pricing (clinical sign-off note on the combination page).
 *
 * Implementation is IntersectionObserver + CSS only, gated behind the
 * pre-hydration .js class (no-JS clients and crawlers see complete static
 * content) and collapsed to instant swaps under prefers-reduced-motion.
 */

const steps = [
  {
    // The consult precedes the four strands and carries no strand number.
    num: null,
    name: "Consult",
    heading: "One assessment, every layer.",
    body: "One consultation covers the full protocol. Your treating nurse assesses structure, laxity, movement, and volume, then maps which treatments belong in your plan, in what order, and how far apart. Sometimes that is all four. Often it is fewer. Occasionally it is none, and we will say so.",
    image: "/images/optimized/silk-treatment-anti-wrinkle-consultation-01.jpg",
    alt: "Consultation at Silk Clinical Aesthetics, Christchurch",
    href: null,
  },
  {
    num: "01",
    name: "Ultraformer MPT",
    heading: "Lift the foundation.",
    body: "Ultraformer MPT is high-intensity focused ultrasound: it concentrates energy at fixed depths, down to the SMAS at 4.5mm, the same structural layer a surgeon lifts, creating precise thermal points that trigger lift and new collagen. It is the lifting tool.",
    image: TREATMENT_HERO_IMAGES["ultraformer-mpt-hifu"],
    alt: "Ultraformer MPT HIFU device at Silk Clinical Aesthetics, Christchurch",
    href: "/treatments/ultraformer-mpt-hifu",
  },
  {
    num: "02",
    name: "Volnewmer",
    heading: "Tighten the dermis.",
    body: "Volnewmer is monopolar radiofrequency: it heats the collagen-rich dermis broadly and evenly, contracting existing collagen fibres immediately and stimulating remodelling over the following months. It is the tightening tool.",
    image: TREATMENT_HERO_IMAGES["volnewmer-rf-skin-tightening"],
    alt: "Volnewmer monopolar RF skin tightening device at Silk Clinical Aesthetics, Christchurch",
    href: "/treatments/volnewmer-rf-skin-tightening",
  },
  {
    num: "03",
    name: "Anti-Wrinkle Injections",
    heading: "Soften the movement.",
    body: "Anti-wrinkle injections address the lines that come from repeated expression, the ones devices cannot reach. Performed by registered nurses, dosed conservatively, and reviewed, so expression stays and only the etching softens. It is the softening tool.",
    image: TREATMENT_HERO_IMAGES["anti-wrinkle-injections"],
    alt: "Anti-wrinkle injection prepared by a registered nurse at Silk Clinical Aesthetics, Christchurch",
    href: "/treatments/anti-wrinkle-injections",
  },
  {
    num: "04",
    name: "Dermal Filler",
    heading: "Refine the detail.",
    body: "Dermal filler restores specific points of lost volume and contour once the structural work has settled: temples, cheeks, chin, jawline, lips where wanted. Placed precisely and sparingly, it is the last pass, not the main event. It is the refining tool.",
    image: TREATMENT_HERO_IMAGES["dermal-fillers"],
    alt: "Dermal filler treatment prepared at Silk Clinical Aesthetics, Christchurch",
    href: "/treatments/dermal-fillers",
  },
];

export function ProtocolSequence({
  eyebrowNum = "05",
  eyebrowLabel = "The Silk Protocol",
  heading = (
    <>
      Four treatments,
      <br />
      one <em className="italic text-gold-bright">weave</em>.
    </>
  ),
  intro = "The Silk Protocol is our complete non-surgical approach to facial ageing. Two devices rebuild the foundation. Two injectable treatments refine the detail. Silk Clinical is the one South Island clinic operating both devices, and the protocol is planned around your face, not a fixed recipe. Few clients need all four; your nurse will tell you honestly which your plan calls for.",
  showStepLinks = false,
}: {
  eyebrowNum?: string;
  eyebrowLabel?: string;
  heading?: ReactNode;
  intro?: ReactNode;
  showStepLinks?: boolean;
} = {}) {
  const [active, setActive] = useState(0);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    // Reveal-failsafe disarm (see globals.css): this section manages its
    // own layer visibility, so the CSS failsafe must stand down once JS
    // is live.
    document.documentElement.classList.add("js-hydrated");

    const els = stepRefs.current.filter(
      (el): el is HTMLDivElement => el !== null
    );
    if (els.length === 0) return;

    // A step is "active" while it crosses the centre band of the
    // viewport. One observer for all steps; a 10% band (rather than a
    // degenerate zero-height line) keeps isIntersecting reliable, and
    // tracking the full intersecting set means fast scrolls and
    // direction changes always resolve to the correct single step.
    const intersecting = new Set<number>();
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const i = els.indexOf(entry.target as HTMLDivElement);
          if (i === -1) continue;
          if (entry.isIntersecting) intersecting.add(i);
          else intersecting.delete(i);
        }
        if (intersecting.size > 0) {
          setActive(Math.max(...intersecting));
        }
      },
      { threshold: 0, rootMargin: "-45% 0px -45% 0px" }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    // No overflow-hidden on the section itself: it would turn the section
    // into the sticky containing scroll box and break position: sticky.
    // The watermark bleed is clipped by its own absolute wrapper below.
    // Paper ground: TrustBar above is oat, so with the treatments grid
    // removed this section takes paper to keep the alternation.
    <section className="relative bg-paper py-16 sm:py-24 lg:py-32">
      {/* Scoped styles for the sequence (globals.css is foundation-owned).
          `href` + `precedence` make React 19 hoist this into <head>. Without
          them the tag renders as a direct child of <section>, where <style>
          is metadata in a flow-content slot: 1 W3C validity error per page,
          10 across the site (audit 2026-08-03, onsite item 8). Hoisting also
          dedupes it when the section appears more than once. */}
      <style href="silk-protocol-sequence" precedence="medium">{`
        /* globals.css sets body { overflow-x: hidden }, which makes body a
           non-scrolling scroll container and silently defeats
           position: sticky for the pinned media panel. overflow-x: clip
           gives the identical horizontal clipping without creating a
           scroll container. Scoped here rather than editing the
           foundation-owned globals.css; only pages with this section are
           affected. */
        body { overflow-x: clip; }
        .js .silk-protocol-layer {
          clip-path: inset(100% 0 0 0);
          transition: clip-path var(--duration-wipe, 1.1s) var(--ease-silk-inout, cubic-bezier(0.65, 0, 0.35, 1));
        }
        .js .silk-protocol-layer img {
          transform: scale(1.06);
          transition: transform var(--duration-wipe, 1.1s) var(--ease-silk-inout, cubic-bezier(0.65, 0, 0.35, 1));
        }
        .js .silk-protocol-layer.is-active {
          clip-path: inset(0 0 0 0);
        }
        .js .silk-protocol-layer.is-active img {
          transform: scale(1);
        }
        .silk-protocol-fill {
          transition: height var(--duration-state, 0.6s) ease;
        }
        @media (prefers-reduced-motion: reduce) {
          .js .silk-protocol-layer,
          .js .silk-protocol-layer img,
          .silk-protocol-fill {
            transition: none;
          }
          .js .silk-protocol-layer img {
            transform: none;
          }
        }
      `}</style>

      {/* Oversized low-opacity SCA monogram motif, bleeding off the edge
          (brief 5.2 secondary motif; one per page). Clipped by its own
          absolute wrapper so the section needs no overflow-hidden. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        <Image
          src="/images/logo/sca-monogram.png"
          alt=""
          width={620}
          height={620}
          className="select-none absolute -right-28 top-0"
          style={{ width: "38rem", height: "auto", opacity: 0.06 }}
        />
      </div>

      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-16">
        {/* Section header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-6 lg:gap-x-12 items-end mb-14 lg:mb-20">
          <div className="lg:col-span-7">
            <Reveal variant="fade">
              <p className="silk-eyebrow">
                <span className="silk-eyebrow-num">{eyebrowNum}</span>
                <span className="silk-eyebrow-rule" />
                <span>{eyebrowLabel}</span>
              </p>
            </Reveal>
            <SplitReveal
              as="h2"
              type="chars"
              delay={0.1}
              className="mt-6 silk-display-2 text-ink"
            >
              {heading}
            </SplitReveal>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <SplitReveal
              as="p"
              type="lines"
              delay={0.35}
              className="silk-body text-ink/70 max-w-md"
            >
              {intro}
            </SplitReveal>
          </div>
        </div>

        {/* Pinned two-column sequence */}
        <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-x-16">
          {/* Steps column with gold progress hairline */}
          <div className="relative lg:col-span-5 pl-8 sm:pl-10">
            {/* Progress track */}
            <div
              aria-hidden="true"
              className="absolute left-0 top-0 bottom-0 w-px bg-gold/25"
            >
              <div
                className="silk-protocol-fill w-px bg-gold"
                style={{ height: `${((active + 1) / steps.length) * 100}%` }}
              />
            </div>

            {steps.map((step, i) => (
              <div
                key={step.name}
                ref={(el) => {
                  stepRefs.current[i] = el;
                }}
                className="py-14 sm:py-16 lg:py-0 lg:min-h-[75vh] lg:flex lg:flex-col lg:justify-center"
              >
                {/* Mobile media (the sticky panel is desktop-only) */}
                <Reveal variant="wipe" className="lg:hidden mb-7">
                  <div className="silk-img-rounded relative aspect-[4/3] overflow-hidden">
                    <Image
                      src={step.image}
                      alt={step.alt}
                      fill
                      className="object-cover"
                      sizes="(max-width: 1024px) 100vw, 0px"
                    />
                  </div>
                </Reveal>

                {/* Strand numbering is part of the section frame; the
                    unnumbered consult block precedes the four strands. */}
                <p
                  className={`silk-eyebrow ${step.num ? "silk-eyebrow--step" : ""}`}
                >
                  {step.num && (
                    <span className="silk-eyebrow-num">{step.num}</span>
                  )}
                  <span className="silk-eyebrow-rule" />
                  <span>{step.name}</span>
                </p>

                {/* Venetian recipe 2: per-character rise as the step
                    scrolls into its own trigger */}
                <SplitReveal
                  as="h3"
                  type="chars"
                  className="mt-5 silk-display-3 text-ink"
                >
                  {step.heading}
                </SplitReveal>

                <p className="mt-5 silk-body text-ink/70 max-w-md">
                  {step.body}
                </p>

                {/* Per-treatment button through to the individual guide.
                    Gated so the homepage sequence stays plain and clinical;
                    the Consult step has no treatment page. */}
                {showStepLinks && step.href && (
                  <Link
                    href={step.href}
                    className="silk-pill silk-pill--secondary self-start mt-7 !px-6 !py-2.5 text-[13px]"
                  >
                    Read the treatment guide
                  </Link>
                )}
              </div>
            ))}

            <div className="pt-2 pb-4 lg:pb-0 lg:pt-0 lg:-mt-16">
              {/* Closing line: the frame's summary, before the guide CTA. */}
              <p className="mb-7 max-w-md border-t silk-hairline pt-6 silk-body text-ink/70">
                The order matters. Foundation before surface, structure
                before detail, and time between each so your skin does the
                work.
              </p>
              {/* Interim destination: the two-device combination guide
                  (which keeps its SEO standing). Repoint to the dedicated
                  Silk Protocol guide page when it exists. */}
              <Link
                href="/volnewmer-ultraformer-combination"
                className="silk-pill silk-pill--secondary"
              >
                Read the Silk Protocol Guide
              </Link>
            </div>
          </div>

          {/* Sticky media panel (desktop) */}
          <div className="hidden lg:block lg:col-span-6 lg:col-start-7">
            <div className="lg:sticky lg:top-28 h-[calc(100vh-9.5rem)]">
              <div className="silk-img-rounded relative h-full overflow-hidden">
                {steps.map((step, i) => (
                  <div
                    key={step.name}
                    className={`silk-protocol-layer absolute inset-0 ${
                      active >= i ? "is-active" : ""
                    }`}
                    style={{ zIndex: i }}
                  >
                    <Image
                      src={step.image}
                      alt={step.alt}
                      fill
                      className="object-cover"
                      // Not 50vw, even though the panel is half the grid.
                      // The panel is tall (100vh minus the sticky offset) and
                      // these sources are 16:9, so object-cover scales to fill
                      // the HEIGHT and crops the sides. The required intrinsic
                      // width is panelHeight x 1.78, not panel width: roughly
                      // 1330px at 1440x900 and 1650px at 1920x1080, against
                      // the ~720px that 50vw asked for. That picked the 828
                      // rung and upscaled it about 1.85x, which read as grain.
                      // 100vw over-fetches against the slot width by design;
                      // it is what the cover crop actually consumes, and it
                      // stays ahead of the requirement up to 2560.
                      sizes="(max-width: 1024px) 0px, 100vw"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
